import VerticalAlignTop from '@mui/icons-material/VerticalAlignTop';
import { IconButton, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import Panel from '../Panel';
import { Stack, styled } from '@mui/system';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import PeerGroupDetails from './PeerGroupDetails';
import Box from '@mui/material/Box';
import { BenchmarkName } from '../text/BenchmarkName';

const BackToTopButton = styled(IconButton)(({ theme }) => ({
    color: theme.palette.orange.main,
    backgroundColor: 'transparent',
    '&:hover': {
        backgroundColor: 'transparent'
    },
    fontFamily: theme.typography.fontFamily
}));

/**
 * Renders the header for the ProDrillDown component.
 * It displays the name of the selected dot and a button to scroll back to the top of the page.
 *
 * @component
 * @param {object} selectedDot - the selected dot to display information about
 * @param peerCount - count of portfolios in current peer group
 * @returns {JSX.Element}
 */
const ProDrillDownHeader = ({ selectedDot, peerCount }) => {
    const theme = useTheme();

    const isBelowSm = useMediaQuery(theme.breakpoints.down('sm'));
    const isBelowMd = useMediaQuery(theme.breakpoints.down('md'));

    const handleBackToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (isBelowSm) {
        return (
            <Box
                data-analyticsid='portfolio-drilldown-header'
                sx={{ textAlign: 'center' }}
            >
                <Typography
                    variant='header2'
                    sx={{ padding: 1 }}
                    data-analyticsid='portfolio-name'
                >
                    {selectedDot?.portfolioLongName}
                </Typography>
                <BenchmarkName
                    portfolioData={selectedDot}
                    data-analyticsid='benchmark-name'
                />
            </Box>
        );
    } else {
        return (
            <Panel
                data-analyticsid='portfolio-drilldown-header'
                flexDirection='column'
                alignItems='center'
                sx={{ paddingLeft: 6, paddingRight: 6 }}
            >
                <Stack width='100%' spacing={3}>
                    <Stack
                        direction='row'
                        width='100%'
                        alignItems='center'
                        justifyContent='space-between'
                    >
                        <Box>
                            <Typography
                                variant='h2'
                                fontWeight={500}
                                data-analyticsid='portfolio-name'
                            >
                                {selectedDot?.portfolioLongName}
                            </Typography>
                            <BenchmarkName portfolioData={selectedDot} />
                        </Box>
                        <BackToTopButton
                            onClick={handleBackToTop}
                            size='small'
                            data-analyticsid='back-to-top-button'
                        >
                            <Typography variant='body2'>Back to Top</Typography>
                            <VerticalAlignTop
                                fontSize={'small'}
                                sx={{ marginLeft: 1 }}
                            />
                        </BackToTopButton>
                    </Stack>
                    {isBelowMd && (
                        <PeerGroupDetails
                            selectedPortfolio={selectedDot}
                            peerCount={peerCount}
                            isHorizontal={true}
                        />
                    )}
                </Stack>
            </Panel>
        );
    }
};

ProDrillDownHeader.propTypes = {
    selectedDot: PropTypes.object,
    peerCount: PropTypes.number
};
export default ProDrillDownHeader;
